import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import CustomDropdown from '../../custom/customDropdown';

/* Čuvanje mesta sa mape — „da se zna gde je i šta je".

   Dva podatka nose sav smisao ovog zapisa: NAZIV (šta je) i TIP (čemu služi).
   Zato su oni jedini obavezni pored koordinata, a adresa i grad dolaze
   popunjeni sa mape i mogu da se isprave, jer OSM ume da vrati ulicu bez broja
   ili naziv opštine umesto grada.

   Koordinate se NE menjaju ručno. Tačka je već postavljena klikom ili
   pretragom; polje za kucanje geografske širine je poziv na grešku koju posle
   niko ne primeti — pogrešna tačka izgleda isto kao tačna dok neko ne krene
   po njoj. Ako tačka nije dobra, zatvori se prozor i klikne na pravo mesto.

   Kad mesto potiče od klijenta iz baze, veza se pamti (id_klijent) — tako se
   isti klijent posle ne nudi dvaput u pretrazi, jednom sa tačkom a jednom bez. */

const PRAZNO = { naziv: '', tip: null, adresa: '', grad: '', drzava: '', napomena: '' };

const DialogLokacija = ({ vidljiv, tacka, tipovi, cuva, onSacuvaj, onZatvori }) => {
    const { t } = useTranslation();
    const [forma, setForma] = useState(PRAZNO);
    const [poslato, setPoslato] = useState(false);

    /* Pri svakom otvaranju se puni iz tačke — isti prozor služi i za adresu sa
       mape i za klijenta iz baze, pa zaostala vrednost iz prošlog otvaranja
       ume da se sačuva pod pogrešnim mestom. */
    useEffect(() => {
        if (!vidljiv || !tacka) return;
        setPoslato(false);
        setForma({
            naziv:  tacka.naziv || '',
            /* Tačka nosi tip kao modgen id, a dropdown traži CEO objekat opcije
               (CustomDropdown ne prosleđuje optionValue). Bez ovog prevođenja
               već sačuvana lokacija bi se otvorila sa praznim tipom i tiho ga
               izgubila pri sledećem čuvanju. */
            tip:    (tipovi || []).find(o => o.value === tacka.tip) || null,
            adresa: tacka.adresa || '',
            grad:   tacka.grad || '',
            drzava: tacka.drzava || '',
            napomena: '',
        });
    }, [vidljiv, tacka, tipovi]);

    const polje = (k) => (e) => setForma(f => ({ ...f, [k]: e.target.value }));

    const nedostajeNaziv = !String(forma.naziv || '').trim();
    const nedostajeTip   = !forma.tip;

    const sacuvaj = () => {
        setPoslato(true);
        if (nedostajeNaziv || nedostajeTip) return;
        onSacuvaj({
            ...forma,
            naziv: String(forma.naziv).trim(),
            tip: forma.tip?.value ?? forma.tip,
            lat: tacka.lat,
            lng: tacka.lng,
            id_klijent: tacka.id_klijent || null,
        });
    };

    const podnozje = (
        <div className="gps-lok-podnozje">
            <Button type="button" label={t('common.cancel')} className="p-button-text p-button-sm" onClick={onZatvori} />
            <Button type="button" icon="pi pi-save" label={t('common.sacuvaj')} severity="success"
                    loading={cuva} onClick={sacuvaj} />
        </div>
    );

    return (
        <Dialog
            header={t('gps.lokacijaNaslov')}
            visible={vidljiv}
            onHide={onZatvori}
            className="gps-dijalog-lokacija"
            style={{ width: '34rem', maxWidth: '95vw' }}
            footer={podnozje}
            draggable={false}
        >
            {/* Tačka stoji na vrhu kao potvrda „ovo čuvaš", ne kao polje za unos. */}
            <div className="gps-lok-koord">
                <i className="pi pi-map-marker" />
                <span>{Number(tacka?.lat).toFixed(5)}, {Number(tacka?.lng).toFixed(5)}</span>
                {!!tacka?.id_klijent && (
                    <span className="gps-lok-veza">{t('gps.lokacijaVezanoZaKlijenta')}</span>
                )}
            </div>

            <div className="gps-lok-mreza">
                <div className="gps-lok-polje gps-lok-siroko">
                    <label htmlFor="lok-naziv">{t('gps.lokacijaNaziv')} <span className="gps-pod-obavezno">*</span></label>
                    <InputText id="lok-naziv" value={forma.naziv} onChange={polje('naziv')}
                               className={poslato && nedostajeNaziv ? 'p-invalid' : ''}
                               placeholder={t('gps.lokacijaNazivPrimer')} />
                </div>

                <div className="gps-lok-polje gps-lok-siroko">
                    <label htmlFor="lok-tip">{t('gps.lokacijaTip')} <span className="gps-pod-obavezno">*</span></label>
                    <CustomDropdown
                        id="lok-tip"
                        name="tip"
                        value={forma.tip}
                        options={tipovi || []}
                        optionLabel="label"
                        onChange={(e) => setForma(f => ({ ...f, tip: e.target.value }))}
                        placeholder={t('gps.lokacijaTipIzaberi')}
                        invalid={poslato && nedostajeTip}
                        emptyMessage={t('gps.lokacijaNemaTipova')}
                        style={{ width: '100%' }}
                    />
                </div>

                <div className="gps-lok-polje gps-lok-siroko">
                    <label htmlFor="lok-adresa">{t('gps.lokacijaAdresa')}</label>
                    <InputText id="lok-adresa" value={forma.adresa} onChange={polje('adresa')} />
                </div>

                <div className="gps-lok-polje">
                    <label htmlFor="lok-grad">{t('gps.lokacijaGrad')}</label>
                    <InputText id="lok-grad" value={forma.grad} onChange={polje('grad')} />
                </div>

                <div className="gps-lok-polje">
                    <label htmlFor="lok-drzava">{t('gps.lokacijaDrzava')}</label>
                    <InputText id="lok-drzava" value={forma.drzava} onChange={polje('drzava')} />
                </div>

                <div className="gps-lok-polje gps-lok-siroko">
                    <label htmlFor="lok-napomena">{t('gps.lokacijaNapomena')}</label>
                    <InputTextarea id="lok-napomena" rows={2} autoResize value={forma.napomena}
                                   onChange={polje('napomena')}
                                   placeholder={t('gps.lokacijaNapomenaPrimer')} />
                </div>
            </div>
        </Dialog>
    );
};

export default DialogLokacija;
